import { APP_BASE_HREF, NgOptimizedImage, PlatformLocation, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeEn from '@angular/common/locales/en';
import localePl from '@angular/common/locales/pl';
import { APP_INITIALIZER, ENVIRONMENT_INITIALIZER, ErrorHandler, inject, NgModule, NgZone, Type } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { of } from 'rxjs';
import 'src/app/shared/helpers/array.helper';
import 'src/app/shared/helpers/string.helper';
import { environment } from 'src/environments/environment';
import { AnonymousPaymentPlanComponent } from './anonymous-payment-plan/anonymous-payment-plan.component';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import { ComparatorResolver } from './dashboard/comparator/comparator.resolver';
import { PaymentsModule } from './dashboard/payments/payments.module';
import { SettingsModule } from './dashboard/profile/settings/settings.module';
import { ProjectResolver } from './dashboard/project/project.resolver';
// import { ShowFeedbackPopupService } from './dashboard/services/show-feedback-popup.service';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions } from '@angular/material/form-field';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MAT_TABS_CONFIG, MatTabsConfig } from '@angular/material/tabs';
import { TitleStrategy } from '@angular/router';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { NgApexchartsModule } from 'ng-apexcharts';
import { EnvironmentTypesEnum } from 'src/environments/env.model';
import { FixComponent } from './fix/fix.component';
import { AuthGuard } from './guards/auth.guard.service';
import { GuestGuard } from './guards/quest.guard.service';
import { SuperuserGuard } from './guards/superuser.guard.service';
import { WhiteLabelGuard } from './guards/whitelabel.guard.service';
import { AuthIntercecptorService } from './interceptors/auth-interceptor.service';
import { ClientVersionIntercecptorService } from './interceptors/client-version-interceptor.service';
import { ErrorIntercecptorService } from './interceptors/error-interceptor.service';
import { MaintenanceComponent } from './maintenance/maintenance.component';
import { NotificationModule } from './notification/notification.module';
import { initPostHog, PostHogInitializer } from './posthog/posthog';
import { MaterialModule } from './shared/material-module/material.module';
import { RefreshAppModalComponent } from './shared/modals/refresh-app-modal/refresh-app-modal.component';
import { MatPaginationIntlService } from './shared/sem-table/table-display/pagination/MatPaginationIntl.service';
import { HelperService } from './shared/service/helper.service';
import { LocalStorageService, storageKey } from './shared/service/local-storage.service';
import { PluginService } from './shared/service/plugin.service';
import { SentryService } from './shared/service/sentry.service';
import { LangEnum } from './shared/service/translation.service';
import { SharedModule } from './shared/shared.module';
import { ShopifyComponent } from './shopify/shopify.component';
import { TranslatedTitleStrategy } from './translated-title-strategy';

export function pluginHandler(pluginService: PluginService) {
  return () => (environment.pluginMode ? pluginService.prepareAppBridge() : of());
}

registerLocaleData(localeEn);
registerLocaleData(localePl);

export const CUSTOM_MAT_FORM_FIELD_DEFAULT_OPTIONS: MatFormFieldDefaultOptions = {
  appearance: 'outline',
  floatLabel: 'always',
};

const CUSTOM_MAT_TABS_CONFIG: MatTabsConfig = {
  stretchTabs: false,
};

function initializeSentry(sentryService: SentryService): () => void {
  return () => sentryService.sentryInit();
}

class CustomMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    if (environment.type === EnvironmentTypesEnum.prod) {
      return;
    }

    const { key, translateService } = params;

    if (key) {
      let msg = `Missing translation: "${params.key}"`;

      if (translateService?.store?.currentLang) {
        msg = `${msg} in language "${translateService.store.currentLang}"`;
      }

      console.error(msg);
      Sentry.captureMessage(msg, 'error');
    }
  }
}

@NgModule({
  declarations: [
    AnonymousPaymentPlanComponent,
    AppComponent,
    FixComponent,
    MaintenanceComponent,
    RefreshAppModalComponent,
    ShopifyComponent,
  ],
  bootstrap: [AppComponent],
  imports: [
    AppRoutingModule,
    BrowserAnimationsModule,
    BrowserModule,
    MaterialModule,
    NotificationModule,
    PaymentsModule,
    SharedModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: () => new TranslateHttpLoader(inject(HttpClient), './assets/i18n/', '.json?v=' + environment.appVersion),
      },
      missingTranslationHandler: { provide: MissingTranslationHandler, useClass: CustomMissingTranslationHandler },
    }),
    SettingsModule,
    AuthModule,
    NgOptimizedImage,
    NgApexchartsModule,
  ],
  providers: [
    AuthGuard,
    GuestGuard,
    WhiteLabelGuard,
    SuperuserGuard,
    SentryService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeSentry,
      deps: [SentryService],
      multi: true,
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [ErrorHandler],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthIntercecptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorIntercecptorService,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ClientVersionIntercecptorService,
      multi: true,
    },
    ProjectResolver,
    ComparatorResolver,
    HelperService,
    {
      provide: APP_BASE_HREF,
      useFactory: (s: PlatformLocation) => s.getBaseHrefFromDOM(),
      deps: [PlatformLocation],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: pluginHandler,
      deps: [PluginService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        if (!environment.production) {
          return () => null;
        }
        const ngZone = inject(NgZone);
        return () => initPostHog(ngZone);
      },
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        const translateService = inject(TranslateService);
        const localStorageService = inject(LocalStorageService);

        return () => translateService.use(localStorageService.get(storageKey.locale) ?? LangEnum.en);
      },
      multi: true,
    },
    { provide: 'googleTagManagerId', useValue: 'GTM-KR75CPG' },
    { provide: TitleStrategy, useClass: TranslatedTitleStrategy },
    { provide: MatPaginatorIntl, useClass: MatPaginationIntlService },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: CUSTOM_MAT_FORM_FIELD_DEFAULT_OPTIONS,
    },
    {
      provide: MAT_TABS_CONFIG,
      useValue: CUSTOM_MAT_TABS_CONFIG,
    },
    {
      provide: ENVIRONMENT_INITIALIZER,
      multi: true,
      useValue: () => {
        // @TODOL @TMP: wyłączenie popupa do momentu wprowadzenia poprawek: https://app.asana.com/0/1206609340772094/1206905953914297
        // const globalServicesTypes: Array<Type<unknown>> = [ShowFeedbackPopupService];
        const globalServicesTypes: Array<Type<unknown>> = [];
        if (environment.production) {
          globalServicesTypes.push(PostHogInitializer);
        }
        globalServicesTypes.forEach((token) => inject(token));
      },
    },
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
